<template>
    <div>
        <div class="InputSimCard" :class="[simFormatError? 'sim-error':'', isSelectSim? '': 'no-select-sim', loading ? 'reo-processing' : '']">
            <div class="step-text">
                <h3>
                    {{ $t('subscribe_plan.input_sim.title') }}
                    <span v-if="!isSelectSim">
                        <span class="card-id" v-if="!isSelectSim">{{ simCardCode }}</span>
                        <button class="button_clear" @click="showInput=!showInput">
                            <CO_EDIT />
                        </button>
                    </span>
                </h3>
                <p v-if="isSelectSim">
                    <i18n path="subscribe_plan.input_sim.scan_or_enter">
                        <span place="tip">
                            <button class="button_clear" @click="showTip=true"><i class="icon icon-co_tips_line"/></button>
                            <reo-dialog
                                v-if="showTip"
                                :labelOfConfirm="$t('cart.switch.ok')"
                                @confirm="showTip=false"
                                @cancel="showTip=false"
                                class="sim-tip-dialog"
                            >
                                <div class="content">
                                    <p>{{ $t('cart.input_sim_tip') }}</p>
                                    <div class="tip-img">
                                    </div>
                                </div>
                            </reo-dialog>
                        </span>
                    </i18n>
                </p>
            </div>
            <div class="step-input" v-show="isSelectSim || showInput">
                <ElInput v-model.trim="simCardCode"
                    size="medium"
                    @blur="validateSimCardNumber"
                    @change="simBindError = false"
                >
                    <div slot="suffix"
                        class="scan-icon el-input__icon"
                        @click="startScanQrCode"
                    >
                        <CO_SCREENSHOT v-if="isMobile"/>
                        <CO_UPLOAD v-else/>
                    </div>
                </ElInput>
                <div class="simCard-error" v-show="simFormatError">{{$t('errors.bind_sim_fail')}}</div>
                <ReoLink
                    v-if="isSelectSim"
                    :text="$t('subscribe_plan.input_sim.choose')"
                    @click="handleShowSelectSim"
                />
            </div>
            <div class="scan-container">
                <div id="scan"></div>
            </div>
            <ReoCommonButton
                :label="$t('common.next')"
                :customWidth="isMobile?'100%':'253'"
                colorType="dark"
                :disabled="simFormatError || simBindError || !verified || emptyInput"
                @click="handleNext"
                v-if="isSelectSim"
            />
            <SelectSimDialog
                v-model="showSelectSim"
                @next="handleNext"
            />
            <img id="preview" ref="preview" v-show="false"/>
            <input
                id="camera"
                ref="camera"
                type="file"
                accept="image/*;text/plain"
                hidden
                @change="handleScanQrCode"
            />
            <div class="input-number-tip">
                <div>{{ $t('cart.input_sim_number_tip') }}</div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { Input as ElInput } from 'element-ui';
import QRReader from '../../lib/qrCodeHandler.js';
import ReoLink from '@/basic/Link.vue';
import { getYmhFromQuery } from '@/common/js/query';
import { CO_EDIT, CO_SCREENSHOT, CO_UPLOAD } from '@/components/icon';
import { mapGetters } from 'vuex';
import { SimManager } from '@/lib/simManager.js';
import SelectSimDialog from './SelectSimDialog.vue';
import { addSim } from '@/api/sim.js';
import { uidHandler } from '@/lib/uidHandler.js';
import { unfoldError } from '@/common/js/notice.js';
import nprogress from 'nprogress';
import { config } from 'CONFIG';
import ReoDialog from '@/basic/Dialog.vue';
import { requestToApp } from '@/api/app.js';
import reolinkGa from '@/common/js/gtagPageview';

export default Vue.extend({
    components: {
        ElInput,
        ReoLink,
        CO_UPLOAD,
        CO_EDIT,
        CO_SCREENSHOT,
        SelectSimDialog,
        ReoDialog
    },
    data() {

        return {
            config,
            simCardCode: getYmhFromQuery( this.$route ),
            showScan: false,
            simFormatError: false,
            simBindError: false,
            showInput: false,
            simInfoArr: [] as SimManager[],
            showSelectSim: false,
            loading: false,
            showTip: false,
        };

    },
    computed: {
        ...mapGetters<'isMobile'>( ['isMobile'] ),
        isSelectSim(): boolean {

            return this.$route.path.includes( 'select-sim' );

        },
        verified (): boolean {

            return this.$store.state.user.user.email.status === 'verified';

        },
        emptyInput(): boolean {

            return !this.simCardCode || this.simCardCode === '';

        }
    },
    watch: {
        emptyInput( newVal: boolean ) {

            if ( newVal ) {

                this.simFormatError = false;

            }

        }
    },
    mounted() {

        QRReader.init();

        nprogress.done();
        Reflect.set( window, 'scanQRCode', this.handleGetScanResult.bind( this ));
        if ( this.simCardCode ) {

            this.handleGetScanResult( this.simCardCode );
        
        }

    },
    methods: {
        startScanQrCode() {

            reolinkGa.sendSimpleEvent( 'sim list-Scan SIM card' );
            if ( config.platform === 'app' ) {

                requestToApp( 'scanQRCode', {}, this.handleGetScanResult.bind( this ));
                return;

            }
            const camera =  this.$refs.camera;
            if ( camera && camera instanceof HTMLInputElement ) {

                camera.click();

            }

        },
        handleScanQrCode( event: Event ) {

            const target = event.target as HTMLInputElement;
            if ( target.files && target.files.length > 0 && target.files[0].type.includes( 'image' )) {

                const frame = this.$refs.preview as HTMLImageElement;
                frame.className = 'app__overlay';
                frame.src = URL.createObjectURL( target.files[0] );
                this.loading = true;
                let exceedTimeLimit = false;
                const timeChecker = setTimeout(() => {

                    this.loading = false;
                    exceedTimeLimit  = true;
                    console.warn( 'QRReader fail: exceed time limit' );
                    unfoldError( 'errors.scan_fail' );

                }, 10000 );
                QRReader.scan(( data ) => {

                    if ( exceedTimeLimit ) {

                        return;

                    }

                    clearTimeout( timeChecker );
                    this.loading = false;

                    if ( typeof data === 'string' && data.length > 0 ) {

                        this.simCardCode = data;
                        this.validateSimCardNumber();

                    } else {

                        console.warn( 'QRReader fail: get nothing' );
                        unfoldError( 'errors.scan_fail' );

                    }

                });

            } else {

                console.warn( 'QRReader fail: not image file' );
                unfoldError( 'errors.scan_fail' );

            }

        },
        validateSimCardNumber() {

            if ( !this.emptyInput ) {

                this.simFormatError =  !/^[a-zA-Z]{4,4}\d{6,6}$/.test( this.simCardCode );

            }

        },
        async handleNext( cardCode?: string ) {

            try {

                reolinkGa.sendSimpleEvent( 'sim list-next' );
                const simCardCode = cardCode ?? this.simCardCode;
                const res = await addSim({ simCardCode  });
                this.simCardCode = simCardCode;
                await this.$router.push({
                    path: '/user/subscribe-plan/sim/choose',
                    query: {
                        ymh: uidHandler.encrypt( res.iccid )
                    }
                });

            } catch ( e ) {

                console.error( e );
                this.simBindError = true;
                unfoldError( 'errors.bind_sim_fail', undefined, true );

            } finally {

                this.showSelectSim = false;

            }

        },
        handleShowSelectSim() {

            reolinkGa.sendSimpleEvent( 'sim list-Select a Card' );
            this.showSelectSim = true;

        },
        handleGetScanResult( result: string ) {

            console.log( `scan result: ${result}` );
            if ( typeof result !== 'string' ) {

                return;

            }
            this.simCardCode = result;
            this.validateSimCardNumber();

            if ( !this.simFormatError ) {

                this.handleNext().catch( console.error );

            }

        }
    }
});
</script>

<style lang="less" scoped>
.InputSimCard{
    // width: 1000px;
    background-color: @bg_02;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 60px 32px;
    gap: 32px;
    @media @mobile{
        box-sizing: border-box;
        width: 100%;
        gap: 24px;
        align-items: flex-start;
        padding: 24px 19px;
    }
    .content-common{
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
    .step-text{
        .content-common();
        @media @pc{
            text-align: center;
        }
        h3{
            font-size: 24px;
            font-weight: 600;
            @media @mobile{
                font-size: 18px;
            }
        }
        p{
            font-size: 16px;
            color: @font_secondary;
            max-width: 710px;
            @media @mobile{
                font-size: 14px;
            }
        }
        button{
            vertical-align: bottom;
            .icon{
                font-size: 20px;
                color: @black;
                @media @mobile{
                    font-size: 15px;
                }
            }
        }
    }
    .step-input{
        .content-common();
        align-items: center;
        text-align: center;
        @media @mobile{
            width: 100%;
            text-align: left;
        }
        /deep/ .el-input {
            width: 360px;
            @media @mobile{
                width: 100%;
            }
            input{
                height: 48px;
                line-height: 14px;
                color: @font_primary;
                font-size: 14px;
                font-weight: 500;
                border-radius: 8px;
            }
        }
        /deep/ .ui-link{
            .text{
                font-size: 16px;
            }
        }
        .simCard-error{
            color: @red_6;
            font-size: 16px;
            font-weight: normal;
            max-width: 710px;
        }
        .scan-icon{
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            &:hover{
                svg{
                    background-color: @fill_tertiary;
                    border-radius: 4px;
                }
            }
        }
    }
    .scan-container{
        position: fixed;
        top: 0;
        left: 0;
        height: 100px;
        width: 100px;
        z-index: -1;
        background-color: @bg_02;
        #scan{
            width: 100%;
            height: 100%;
        }
    }

}

.sim-error{
    /deep/ .el-input{
        input{
            border-color: @red_6;
        }
    }
}

.no-select-sim{
    padding: 32px;
    align-items: flex-start;
    @media @mobile{
        padding: 16px;
    }
    .step-text{
        h3{
            font-size: 20px;
            display: flex;
            gap: 8px;
            align-items: center;
            @media @mobile{
                flex-direction: column;
                align-items: flex-start;
            }
            span{
                color: @b_6;
                display: flex;
                gap: 8px;
                align-items: center;
                svg{
                    color: @b_6;
                    cursor: pointer;
                }
            }
        }
    }
}

.sim-tip-dialog.reo-dialog{
    /deep/ .reo-dialog-inner{
        @media @pc{
            max-width: unset;
        }
        @media @mobile{
            padding: 12px;
        }
        .btnBox-horiz {
            @media @pc{
                justify-content: center;
            }
        }
        .close{
            @media @mobile{
                display: none;
            }
        }
    }

    .content{
        display: flex;
        gap: 12px;
        flex-direction: column;
        @media @mobile{
            flex-direction: column-reverse;
        }
        p{
            font-size: 24px;
            font-weight: 500;
            max-width: 800px;
            color: @font_primary;
            text-align: center;
            @media @mobile{
                font-size: 14px;
            }
        }
        .tip-img{
            width: 800px;
            background-color: @bg_03_h;
            height: 226px;
            background-position: center top;
            background-repeat: no-repeat;
            background-image: url('IMAGE/cart/simTip.webp');
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 8px;
            @media @mobile{
                width: 100%;
            }
            img{
                max-width: 530px;
                min-width: 530px;
                aspect-ratio: 530/382.28;
                height: auto;
                @media (max-width: 643px){
                    min-width: 100%;
                }
            }

        }
    }
}

/deep/ .el-input__suffix{
    right: 16px;
}

.input-number-tip{
    display: flex;
    flex-direction: column;
    width: 100%;
    box-sizing: border-box;
    align-items: center;
    justify-content: fle;
    padding: 24px;
    background-color: @bg_01;
    border-radius: 8px;
    color: @font_secondary;
    font-size: 18px;
    font-weight: 500;
    height: 320px;
    background-image: url('IMAGE/cart/simTip.webp');
    background-position: center top 94px;
    background-repeat: no-repeat;
    .mobile({
        background-position: center top 117px;
    });
}
</style>
